import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { getErrorDetails } from "../../app/appUtils";
import AppButton from "../../components/ui/AppButton";
import AppDatePicker from "../../components/ui/AppDatePicker";
import { Spinner } from "../../components/ui/common";
import { Modal } from "../../components/ui/Modal";
import SuccessView from "../../components/ui/SuccessView";
import { useToast } from "../../hooks/useToast";
import DriverDropdownMenu from "../driver/ui/DriverDropdownMenu";
import { VehicleAssignment } from "./@types";
import VehicleDropdownMenu from "./ui/VehicleDropdownMenu";
import {
  useAssignVehicleMutation,
  useUpdateAssignmentMutation,
} from "./vehicleApiSlice";

export const userFormSchema = z.object({
  vehicleId: z.string().nonempty("Vehicle is required"),
  driverId: z.string().nonempty("Driver is required"),
  startDate: z.string().nonempty("Start date is required"),
  endDate: z.string().nonempty("End date is required"),
});

export type UserFormData = z.infer<typeof userFormSchema>;

interface VehicleAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  vehicleAssignment?: VehicleAssignment | null;
  vehicleId?: string | null;
  mode?: "add" | "edit";
}

const VehicleAssignmentModal: React.FC<VehicleAssignmentModalProps> = ({
  isOpen,
  onClose,
  vehicleAssignment = null,
  mode = vehicleAssignment ? "edit" : "add",
  vehicleId = null,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const toast = useToast();
  const isEditMode = mode === "edit" && vehicleAssignment;

  const [
    assignVehicle,
    {
      isLoading: isCreating,
      isSuccess: createSuccess,
      isError: createError,
      error: createErrorData,
      reset: resetCreate,
    },
  ] = useAssignVehicleMutation();

  const [
    updateAssignment,
    {
      isLoading: isUpdating,
      isSuccess: updateSuccess,
      isError: updateError,
      error: updateErrorData,
      reset: resetUpdate,
    },
  ] = useUpdateAssignmentMutation();

  // Derived states for cleaner code
  const isLoading = isCreating || isUpdating;
  const isSuccess = createSuccess || updateSuccess;
  const isError = createError || updateError;
  const error = createErrorData || updateErrorData;

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    setValue,
    reset,
  } = useForm<UserFormData>({
    resolver: zodResolver(userFormSchema),
    mode: "onChange",
    defaultValues: {
      vehicleId: "",
      driverId: "",
      startDate: "",
      endDate: "",
    },
  });

  // Populate form with vehicleAssignment data when editing
  useEffect(() => {
    if (isEditMode && vehicleAssignment) {
      reset({
        vehicleId: vehicleAssignment.vehicleId,
        driverId: vehicleAssignment.driverId,
        startDate: vehicleAssignment.startDate,
        endDate: vehicleAssignment.endDate,
      });
    } else if (!isEditMode) {
      // Reset to empty when switching to add mode
      reset({
        vehicleId: "",
        driverId: "",
        startDate: "",
        endDate: "",
      });
    }
    if (vehicleId) {
      setValue("vehicleId", vehicleId);
    }
  }, [isEditMode, vehicleAssignment, reset]);

  const onSubmit = async (data: UserFormData) => {
    if (isSubmitting || isLoading) return;

    // Prevent submission if editing and no changes were made
    if (isEditMode && !isDirty) {
      toast.warning({
        title: "No Changes",
        message: "No changes were made to update.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        vehicleId: data.vehicleId,
        driverId: data.driverId,
        startDate: data.startDate,
        endDate: data.endDate,
      };

      if (isEditMode && vehicleAssignment?.id) {
        await updateAssignment({
          id: vehicleAssignment.id,
          ...payload,
        }).unwrap();
      } else {
        await assignVehicle(payload).unwrap();
      }

      setShowSuccess(true);
    } catch (err) {
      console.error(`${isEditMode ? "Update" : "Create"} error:`, err);
      const errorMessage =
        getErrorDetails(err).message ||
        `Failed to ${
          isEditMode ? "update" : "add"
        } vehicle assignment. Please try again.`;

      toast.error({
        title: "Error",
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isSuccess && !showSuccess) {
      toast.success({
        title: "Success",
        message: `Vehicle assignment ${
          isEditMode ? "updated" : "added"
        } successfully`,
      });
      setShowSuccess(true);
    }

    if (isError) {
      const errorMessage =
        getErrorDetails(error).message ||
        `Failed to ${
          isEditMode ? "update" : "add"
        } vehicle assignment. Please try again.`;

      toast.error({
        title: "Error",
        message: errorMessage,
      });
    }
  }, [isSuccess, isError, error, toast, isEditMode, showSuccess]);

  const handleCloseModal = () => {
    setShowSuccess(false);
    reset();
    resetCreate();
    resetUpdate();
    onClose();
  };

  const renderSuccessView = () => {
    if (showSuccess) {
      return (
        <SuccessView
          handleCloseModal={handleCloseModal}
          heading=""
          message={`Vehicle assignment has been ${
            isEditMode ? "updated" : "added"
          } successfully.`}
        />
      );
    }
    return null;
  };

  const modalTitle = showSuccess ? "" : `Assign driver`;
  const submitButtonText = isEditMode ? "Assign" : "Assign";

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseModal}
      title={modalTitle}
      className="max-w-full sm:max-w-[80vw] lg:max-w-[45vw] w-full bg-white pb-8"
    >
      {isSuccess ? (
        renderSuccessView()
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 p-6">
          <div className="">
            <VehicleDropdownMenu
              value={watch("vehicleId")}
              onChange={(value) =>
                setValue("vehicleId", value, {
                  shouldValidate: true,
                })
              }
              className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] mb-6"
              error={errors.vehicleId?.message}
            />
          </div>

          <div className="">
            <DriverDropdownMenu
              value={watch("driverId")}
              onChange={(value) =>
                setValue("driverId", value, {
                  shouldValidate: true,
                })
              }
              className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] mb-6"
              error={errors.driverId?.message}
              placeholder="Select a driver"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Controller
              name="startDate"
              control={control}
              render={({ field }) => (
                <AppDatePicker
                  label="Start date"
                  value={field.value ? new Date(field.value) : null}
                  onChange={(date) =>
                    field.onChange(date ? date.toLocaleDateString("en-CA") : "")
                  }
                  error={errors.endDate?.message}
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                  minDate={new Date()}
                />
              )}
            />
            <Controller
              name="endDate"
              control={control}
              render={({ field }) => (
                <AppDatePicker
                  label="End date"
                  value={field.value ? new Date(field.value) : null}
                  onChange={(date) =>
                    field.onChange(date ? date.toLocaleDateString("en-CA") : "")
                  }
                  error={errors.endDate?.message}
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                  minDate={new Date()}
                />
              )}
            />
          </div>

          <div className="flex justify-center items-center space-x-4 text-[15px]">
            <AppButton
              type="button"
              onClick={handleCloseModal}
              disabled={isSubmitting || isLoading}
              className="px-6 py-2 bg-[#F6F6F6] rounded-md text-hColor hover:bg-gray-50 transition-colors text-[15px] disabled:opacity-50"
            >
              Cancel
            </AppButton>

            <AppButton
              type="submit"
              disabled={isSubmitting || isLoading}
              className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/75 transition-colors disabled:opacity-50 text-[15px]"
            >
              {isSubmitting || isLoading ? (
                <>
                  <Spinner /> Adding
                </>
              ) : (
                submitButtonText
              )}
            </AppButton>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default VehicleAssignmentModal;
